if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.studentFieldsReady = false;
        requestAnimationFrame(() => this.initStudentFields());
        this.studentFieldsObserver = new MutationObserver(() => this.initStudentFields());
        this.studentFieldsObserver.observe(this.form, { childList: true, subtree: true });
      }


      initStudentFields() {
        if (this.studentFieldsReady) return;

        const nameStorage = this.form.querySelector('textarea[name*="Student Name"]');
        if (!nameStorage) return;

        this.studentFieldsReady = true;
        this.studentFieldsObserver?.disconnect();
        this.studentNameStorage = nameStorage;
        this.studentEmailStorage = this.form.querySelector('textarea[name*="Student Email"]');
        this.studentQuantityInput = this.form.elements.namedItem('quantity');
        this.studentNamesWrapper = nameStorage.closest('.student-enrollment') || nameStorage.parentElement;
        this.studentEmailWrapper = this.studentEmailStorage?.closest('.ifOnline-lecture') || this.studentEmailStorage?.parentElement;
        const productTitle = this.closest('product-info')?.querySelector('.product__title')?.textContent || document.querySelector('main h1')?.textContent || '';
        this.requiresStudentEmail = Boolean(this.studentEmailStorage && /initial certification|online lecture/i.test(productTitle));

        nameStorage.required = false;
        nameStorage.setAttribute('aria-hidden', 'true');
        nameStorage.parentElement.style.display = 'none';

        if (this.studentEmailStorage) {
          this.studentEmailStorage.required = false;
          this.studentEmailStorage.setAttribute('aria-hidden', 'true');
          this.studentEmailWrapper.style.display = 'none';
        }

        const heading = this.studentNamesWrapper.querySelector('label');
        if (heading) heading.textContent = this.requiresStudentEmail ? 'Student information' : 'Student names';

        this.studentRows = document.createElement('div');
        this.studentRows.className = 'opaque-student-rows';
        this.studentRows.setAttribute('aria-live', 'polite');
        this.studentNamesWrapper.appendChild(this.studentRows);

        this.studentError = document.createElement('div');
        this.studentError.className = 'opaque-student-error';
        this.studentError.setAttribute('role', 'alert');
        this.studentError.hidden = true;
        this.studentNamesWrapper.appendChild(this.studentError);

        if (!document.getElementById('opaque-student-fields-style')) {
          const style = document.createElement('style');
          style.id = 'opaque-student-fields-style';
          style.textContent = `
            .opaque-student-rows{display:grid;gap:1.2rem;margin-top:.8rem}
            .opaque-student-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:1rem;padding:1.2rem;border:1px solid rgba(var(--color-foreground),.25);background:rgba(var(--color-foreground),.025)}
            .opaque-student-row__title{grid-column:1/-1;font-weight:700;font-size:1.5rem}
            .opaque-student-field{display:grid;gap:.45rem}
            .opaque-student-field label{font-size:1.35rem;font-weight:600}
            .opaque-student-field input{width:100%;min-height:4.8rem;padding:1rem 1.2rem;border:1px solid rgba(var(--color-foreground),.55);border-radius:0;background:rgb(var(--color-background));color:rgb(var(--color-foreground));font:inherit}
            .opaque-student-field input[aria-invalid="true"]{border-color:#b42318;box-shadow:0 0 0 1px #b42318}
            .opaque-student-help{margin:.8rem 0 0;font-size:1.3rem;color:rgba(var(--color-foreground),.72)}
            .opaque-student-error{margin-top:1rem;padding:1rem 1.2rem;border-left:4px solid #b42318;background:#fff1f0;color:#7a271a;font-weight:700}
            @media(max-width:749px){.opaque-student-row{grid-template-columns:1fr}}
          `;
          document.head.appendChild(style);
        }

        const help = document.createElement('p');
        help.className = 'opaque-student-help';
        help.textContent = this.requiresStudentEmail
          ? 'Enter one full name and one unique email address for each student.'
          : 'Enter one full name for each student.';
        this.studentNamesWrapper.appendChild(help);

        this.studentQuantityInput?.addEventListener('change', () => this.renderStudentRows());
        this.studentQuantityInput?.addEventListener('input', () => this.renderStudentRows());
        this.renderStudentRows();

        const acceleratedCheckout = this.closest('product-info')?.querySelector('.shopify-payment-button');
        if (acceleratedCheckout) acceleratedCheckout.style.display = 'none';
      }

      getStudentQuantity() {
        return Math.max(1, Math.min(50, Number.parseInt(this.studentQuantityInput?.value || '1', 10) || 1));
      }

      renderStudentRows() {
        if (!this.studentRows) return;

        const existingNames = Array.from(this.studentRows.querySelectorAll('[data-student-name]')).map((input) => input.value);
        const existingEmails = Array.from(this.studentRows.querySelectorAll('[data-student-email]')).map((input) => input.value);
        const storedNames = this.studentNameStorage.value.split(/[,\\n]+/).map((value) => value.trim()).filter(Boolean);
        const storedEmails = this.studentEmailStorage?.value.split(/[,\\n]+/).map((value) => value.trim()).filter(Boolean) || [];
        const quantity = this.getStudentQuantity();

        this.studentRows.replaceChildren();

        for (let index = 0; index < quantity; index += 1) {
          const row = document.createElement('div');
          row.className = 'opaque-student-row';

          const title = document.createElement('div');
          title.className = 'opaque-student-row__title';
          title.textContent = `Student ${index + 1}`;
          row.appendChild(title);

          const nameField = document.createElement('div');
          nameField.className = 'opaque-student-field';
          const nameLabel = document.createElement('label');
          nameLabel.htmlFor = `OpaqueStudentName-${this.dataset.sectionId || 'product'}-${index}`;
          nameLabel.textContent = 'Full name';
          const nameInput = document.createElement('input');
          nameInput.type = 'text';
          nameInput.id = nameLabel.htmlFor;
          nameInput.placeholder = 'First and last name';
          nameInput.autocomplete = 'name';
          nameInput.required = true;
          nameInput.dataset.studentName = '';
          nameInput.value = existingNames[index] ?? storedNames[index] ?? '';
          nameInput.addEventListener('input', () => this.syncStudentProperties());
          nameField.append(nameLabel, nameInput);
          row.appendChild(nameField);

          if (this.requiresStudentEmail) {
            const emailField = document.createElement('div');
            emailField.className = 'opaque-student-field';
            const emailLabel = document.createElement('label');
            emailLabel.htmlFor = `OpaqueStudentEmail-${this.dataset.sectionId || 'product'}-${index}`;
            emailLabel.textContent = 'Email address';
            const emailInput = document.createElement('input');
            emailInput.type = 'email';
            emailInput.id = emailLabel.htmlFor;
            emailInput.placeholder = 'student@example.com';
            emailInput.autocomplete = 'email';
            emailInput.required = true;
            emailInput.dataset.studentEmail = '';
            emailInput.value = existingEmails[index] ?? storedEmails[index] ?? '';
            emailInput.addEventListener('input', () => this.syncStudentProperties());
            emailField.append(emailLabel, emailInput);
            row.appendChild(emailField);
          }

          this.studentRows.appendChild(row);
        }

        this.syncStudentProperties();
      }

      syncStudentProperties() {
        if (!this.studentRows) return;
        const names = Array.from(this.studentRows.querySelectorAll('[data-student-name]')).map((input) => input.value.trim());
        this.studentNameStorage.value = names.filter(Boolean).join(', ');

        if (this.studentEmailStorage) {
          const emails = Array.from(this.studentRows.querySelectorAll('[data-student-email]')).map((input) => input.value.trim());
          this.studentEmailStorage.value = emails.filter(Boolean).join(', ');
        }
      }

      validateStudentFields() {
        this.initStudentFields();
        if (!this.studentRows) return true;

        if (this.studentRows.querySelectorAll('.opaque-student-row').length !== this.getStudentQuantity()) {
          this.renderStudentRows();
        }

        const nameInputs = Array.from(this.studentRows.querySelectorAll('[data-student-name]'));
        const emailInputs = Array.from(this.studentRows.querySelectorAll('[data-student-email]'));
        const invalid = [];
        nameInputs.forEach((input) => {
          input.setAttribute('aria-invalid', String(!input.value.trim()));
          if (!input.value.trim()) invalid.push(input);
        });

        const seenEmails = new Set();
        let duplicateEmail = false;
        emailInputs.forEach((input) => {
          const email = input.value.trim().toLowerCase();
          const bad = !email || !input.checkValidity() || seenEmails.has(email);
          if (email && seenEmails.has(email)) duplicateEmail = true;
          if (email) seenEmails.add(email);
          input.setAttribute('aria-invalid', String(bad));
          if (bad) invalid.push(input);
        });

        if (invalid.length) {
          this.studentError.textContent = duplicateEmail
            ? 'Each student needs a different email address.'
            : `Please complete the required information for all ${this.getStudentQuantity()} students.`;
          this.studentError.hidden = false;
          invalid[0].focus();
          return false;
        }

        this.studentError.hidden = true;
        this.syncStudentProperties();
        return true;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.validateStudentFields()) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
